<template>
  <div class="d-flex">
    <AttachmentItem
      v-for="attachment in attachments"
      class="me-4"
      :attachment="attachment"
      :isDownloading="attachmentDownloadingId === attachment.id"
      :key="`attachment-item-${attachment.id}`"
      :readOnly="readOnly"
      @download="handleDownload(attachment)"
      @remove="handleRemove(attachment)"
    />

    <AttachmentUploader
      v-if="!readOnly && canAddMoreAttachments"
      :isUploading="isUploading"
      @filesSelected="handleFilesSelected"
    />

    <DeleteConfirmationModal
      v-if="isDeleteConfirmationOpen"
      :show="isDeleteConfirmationOpen"
      :title="$t('attachments.delete_file')"
      :text="$t('attachments.delete_file_confirmation')"
      @cancel="isDeleteConfirmationOpen = false"
      @delete="removeSelectedAttachment"
    />
  </div>
</template>

<script>
import ReverMediaSDK from '@/lib/ReverMediaSDK'

import mixin from '@/mixins/mixin.vue'

import DeleteConfirmationModal from '@/components/Modals/DeleteConfirmationModal.vue'
import AttachmentUploader from './AttachmentUploader.vue'
import AttachmentItem from './AttachmentItem.vue'

export default {
  name: 'ThumbnailAttachments',

  mixins: [mixin],

  components: {
    AttachmentItem,
    AttachmentUploader,
    DeleteConfirmationModal,
  },

  props: {
    attachments: {
      type: Array,
      default: () => [],
    },

    readOnly: {
      type: Boolean,
      required: false,
      default: false,
    },

    maxAttachments: {
      type: Number,
      required: false,
      default: 5,
    },
  },

  data() {
    return {
      attachmentDownloadingId: null,
      isUploading: false,
      isDeleteConfirmationOpen: false,
      selectedAttachmentMediaToDelete: null,
    }
  },

  computed: {
    canAddMoreAttachments() {
      return this.attachments.length < this.maxAttachments
    },
  },

  methods: {
    handleFilesSelected(files) {
      if (!this.totalAttachmentsWillBeValid(files)) {
        this.showError(this.$t('routines.approval.invalid_attachments_total'))
        return
      }

      this.uploadSelectedFiles(files)
    },

    totalAttachmentsWillBeValid(newFiles) {
      const totalAttachments =
        (this.attachments?.length ?? 0) + (newFiles?.length ?? 0)

      return totalAttachments <= this.maxAttachments
    },

    async uploadSelectedFiles(files = []) {
      try {
        this.isUploading = true
        const promises = files.map(ReverMediaSDK.uploadMedia)
        const uploadedMedia = await Promise.all(promises)
        const newAttachments = uploadedMedia.map(media => ({
          media,
        }))

        this.$emit('change', [...this.attachments, ...newAttachments])
      } catch (err) {
        this.showError(this.$t('attachments.uploading_the_file_failed'))
      } finally {
        this.isUploading = false
      }
    },

    async handleDownload(attachment) {
      try {
        this.attachmentDownloadingId = attachment.id
        await ReverMediaSDK.download(attachment?.media)
      } catch (error) {
        this.showError(this.$t('attachments.downloading_the_file_failed'))
      } finally {
        this.attachmentDownloadingId = null
      }
    },

    handleRemove(attachment) {
      this.selectedAttachmentMediaToDelete = attachment?.media?.id
      this.isDeleteConfirmationOpen = true
    },

    removeSelectedAttachment() {
      this.$emit(
        'change',
        this.attachments.filter(
          attachment =>
            attachment?.media?.id !== this.selectedAttachmentMediaToDelete
        )
      )

      this.isDeleteConfirmationOpen = false
      this.selectedAttachmentMediaToDelete = null
    },
  },
}
</script>
